import React, { useEffect, useState } from 'react';
import { EtiquetaTentativa } from '../ordenes/FechaTentativa';
import { LuMapPin, LuClock, LuArrowRight, LuTrash2, LuCar, LuCircleCheck, LuCalendarClock, LuCirclePause, LuPencil, LuFileText } from 'react-icons/lu';
import { pdfDeTrabajo } from '../../utils/pdf/trabajo';
import { prepararPDF } from '../../utils/pdf/index.js';
import { trabajoHecho } from '../../utils/pdf/pdfShared';
import PedidoVsHecho from '../ui/PedidoVsHecho';
import ReclamoEnFicha from '../ordenes/ReclamoEnFicha';
import { fueOtraCosa, corto } from '../../utils/pedidoVsHecho';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import ChatVisita from '../ordenes/ChatVisita';
import ModalRegistrarTrabajo from '../ordenes/ModalRegistrarTrabajo';
import CargaPorSerieSheet from '../ordenes/CargaPorSerieSheet';
import ConfirmarHorarioSheet from '../servicio/ConfirmarHorarioSheet';
import ReprogramarSheet from '../ordenes/ReprogramarSheet';
import { resumenVentanas, salirAhoraACoordinar, horaTexto, tituloSinCliente } from '../../utils/ordenes';
import AvatarTecnico from '../ui/AvatarTecnico';
import { M } from '../servicio/ServicioUI';
import { estadoLabel, etapaColor } from '../../utils/estados';
import { fechaAR } from '../../utils/dateUtils';
import { totalServicio } from '../../utils/descuento';
import AntesDeIr from '../ordenes/AntesDeIr';
import { waNumero, mensajeConfirmar } from '../ordenes/SinConfirmarSheet';

// Ficha de una visita de la agenda del Panel (5-oct-2026). Antes, al tocarla,
// llevaba a Trabajos sin abrir nada (y si ya estaba hecha o cobrada, ni
// aparecía en "Por hacer"). Ahora muestra todo ahí mismo: estado de la visita,
// estado del trabajo, monto y qué se hizo.
const MODALIDAD = { EFECTIVO_SIN_FACTURA: 'Efectivo', CON_FACTURA: 'Con factura', TRANSFERENCIA: 'Transferencia' };

function Chip({ estado }) {
    const c = etapaColor(estado);
    return (
        <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-label font-black uppercase tracking-wide"
            style={{ color: c, background: `color-mix(in srgb, ${c} 14%, transparent)` }}>
            {estadoLabel(estado)}
        </span>
    );
}

// Pasos de la visita desde el admin (10-oct-2026): lo mismo que hace el técnico desde su usuario
const PASO_VISITA = {
    PENDIENTE: { estado: 'EN_CAMINO', label: 'Salió', Icon: LuCar },
    EN_CAMINO: { estado: 'EN_SITIO', label: 'Llegó', Icon: LuMapPin, atras: 'PENDIENTE' },
    EN_SITIO:  { cerrar: true, label: 'Cerrar trabajo', Icon: LuCircleCheck, atras: 'EN_CAMINO' },
};

export default function FichaVisitaSheet({ orden: inicial, onCerrar, onVerTrabajos, onEliminada, onCambio }) {
    const [orden, setOrden] = useState(inicial);
    const [moviendo, setMoviendo] = useState(false);
    const [cerrando, setCerrando] = useState(false);
    const [eligiendoDia, setEligiendoDia] = useState(false);   // visita "a coordinar" de un pedido de empresa
    const [servicio, setServicio] = useState(null);
    const [error, setError] = useState(null); // 'borrado' | mensaje
    const [confirmar, setConfirmar] = useState(false);
    const [borrando, setBorrando] = useState(false);
    // 27-oct-2026: pasar la visita a otro día/hora, o dejarla en espera hasta que el cliente confirme
    const [reprogramando, setReprogramando] = useState(false);
    const [tecnicos, setTecnicos] = useState([]);
    const [espera, setEspera] = useState(false);    // confirmación de "Dejar en espera"
    const [dirEdit, setDirEdit] = useState(null);   // texto mientras se edita la dirección (null = no se edita)
    const guardarDireccion = async () => {
        const nueva = (dirEdit || '').trim();
        if (!nueva) { toast.error('Escribí la dirección'); return; }
        if (nueva === (orden.direccion || '').trim()) { setDirEdit(null); return; }
        setMoviendo(true);
        try {
            const r = await api.put(`/ordenes/${orden.id}`, {
                tecnicoId: orden.tecnicoId,
                titulo: orden.titulo || 'Visita',
                descripcion: orden.descripcion || null,
                direccion: nueva,
                clienteId: orden.clienteId || null,
                clienteNombre: orden.clienteNombre || '',
                clienteTelefono: orden.clienteTelefono || '',
                prioridad: orden.prioridad || 'NORMAL',
                fechaProgramada: String(orden.fechaProgramada || '').slice(0, 10),
                horaEstimada: orden.horaEstimada || null,
                montoEstimado: orden.montoEstimado ?? null,
                formaPago: orden.formaPago || null,
                presupuestoId: orden.presupuestoId || null,
                equiposSerie: orden.equiposSerie ?? null,
            });
            setOrden(o => ({ ...o, ...(r.data || { direccion: nueva }) }));
            setDirEdit(null);
            toast.success(`Dirección cambiada${orden.tecnicoNombre ? `: le avisamos a ${orden.tecnicoNombre.split(' ')[0]}` : ''}`);
            onCambio?.();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo cambiar la dirección');
        } finally { setMoviendo(false); }
    };
    useEffect(() => {
        if (reprogramando && tecnicos.length === 0) api.get('/ordenes/tecnicos').then(r => setTecnicos(r.data || [])).catch(() => {});
    }, [reprogramando, tecnicos.length]);
    const dejarEnEspera = async (motivo = null) => {
        setMoviendo(true);
        try {
            const r = await api.post(`/ordenes/${orden.id}/en-espera`, motivo ? { motivo } : {});
            if (motivo) {
                toast.success(r.data?.pedido ? 'No estaba: volvió a "Por agendar" y la empresa ya tiene el aviso' : 'No estaba: quedó para reprogramar', { duration: 5000 });
                onCambio?.();
                if (r.data?.pedido) onEliminada?.(); else setOrden(o => ({ ...o, ...(r.data?.orden || {}) }));
                return;
            }
            const quien = (orden.tecnicoNombre || 'el técnico').split(' ')[0];
            if (r.data?.pedido) {
                toast.success(`En espera: volvió a "Por agendar". Le avisamos a ${quien}`, { duration: 5000 });
                onCambio?.();
                onEliminada?.();   // la visita ya no está en la agenda: se cierra la ficha y se recarga
            } else {
                setOrden(o => ({ ...o, ...(r.data?.orden || {}) }));
                toast.success(`En espera: queda como fecha tentativa. ${quien} no va hasta que confirmes`, { duration: 5000 });
                onCambio?.();
            }
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo dejar en espera');
        } finally { setMoviendo(false); setEspera(false); }
    };

    const eliminar = async () => {
        setBorrando(true);
        try {
            await api.delete(`/ordenes/${orden.id}`);
            toast.success('Visita eliminada');
            onEliminada?.();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo eliminar');
            setBorrando(false);
        }
    };
    // El trabajo de la visita: el presupuesto, o el que se cargó al cerrarla (21-oct-2026: las cerradas
    // por N/S o sin presupuesto decían "Visita suelta" y "Ver en Trabajos" no llevaba al trabajo)
    const trabajoId = orden.servicioId || orden.presupuestoId;
    const [cargando, setCargando] = useState(!!trabajoId);

    useEffect(() => {
        if (!trabajoId) return;
        api.get(`/servicios/${trabajoId}`)
            .then(r => { setServicio(r.data); prepararPDF(r.data); })
            .catch(e => { setServicio(null); setError(e?.response?.status === 404 ? 'borrado' : (e?.response?.data?.mensaje || 'No se pudo cargar el trabajo.')); })
            .finally(() => setCargando(false));
    }, [trabajoId]);

    // "10:30:00" → "10:30"; una franja ("Mañana") queda entera (antes salía "Mañan")
    const hora = horaTexto(orden.horaEstimada) || 'Sin horario';
    // El cierre con presupuesto se hace desde Trabajos (cobro, descuentos…)
    // Visita "a coordinar" (15-oct-2026): el primer paso es elegir el día dentro de lo que marcó la empresa;
    // antes la ficha no mostraba ningún botón y no se sabía qué tocar
    const aCoordinar = !!orden.horarioACoordinar && orden.estado === 'PENDIENTE';
    const paso = aCoordinar ? { salirAhora: true, label: 'Salgo ahora', Icon: LuCar } : PASO_VISITA[orden.estado];
    const salirAhora = async () => {
        setMoviendo(true);
        try {
            const o = await salirAhoraACoordinar(api, orden.id);
            setOrden(x => ({ ...x, ...(o || {}), estado: 'EN_CAMINO', horarioACoordinar: false }));
            toast.success('En camino');
            onCambio?.();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo marcar la salida', { duration: 6000 });
            setEligiendoDia(true);
        } finally { setMoviendo(false); }
    };
    const pasoVisible = paso && !(paso.cerrar && orden.presupuestoId);
    const mover = async (estado) => {
        setMoviendo(true);
        try {
            const r = await api.patch(`/ordenes/${orden.id}/estado`, { estado });
            setOrden(o => ({ ...o, ...(r.data || { estado }) }));
            onCambio?.();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo actualizar la visita');
        } finally { setMoviendo(false); }
    };
    // Fecha tentativa: confirmar (le avisa al técnico) o volver a tentativa
    const cambiarTentativa = async (sinConfirmar) => {
        setMoviendo(true);
        try {
            const r = await api.patch(`/ordenes/${orden.id}/fecha-tentativa`, { sinConfirmar });
            setOrden(o => ({ ...o, ...r.data }));
            toast.success(sinConfirmar ? 'Quedó como fecha tentativa' : `Confirmada: le avisamos a ${(orden.tecnicoNombre || 'el técnico').split(' ')[0]}`);
            onCambio?.();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo cambiar');
        } finally { setMoviendo(false); }
    };
    const abierta = ['PENDIENTE', 'NO_ATENDIDO'].includes(orden.estado);
    // Llevar a ESE trabajo dentro de Trabajos (se abre su ficha en la pestaña donde está)
    const verEnTrabajos = () => {
        if (trabajoId) {
            try { sessionStorage.setItem('trabajosAbrir', JSON.stringify({ servicioId: trabajoId, en: Date.now() })); } catch { /* sin storage: abre la lista */ }
            window.dispatchEvent(new CustomEvent('trabajos:abrir', { detail: { servicioId: trabajoId } }));
        }
        onVerTrabajos();
    };
    const total = servicio
        ? (Number(servicio.montoFinal) > 0 ? Number(servicio.montoFinal) : totalServicio(servicio))
        : 0;
    const fila = 'flex items-center justify-between gap-3 py-2.5 border-b border-black/[0.06] dark:border-white/[0.06] last:border-0';
    // 28-oct-2026: lo que pidieron y lo que se hizo (pidieron limpieza y fue un retiro)
    const pidieron = orden.motivo || servicio?.pedidoMotivo || null;
    const seHizo = servicio && trabajoHecho(servicio)
        ? [...new Set((servicio.items || []).map(it => String(it.trabajoRealizado || '').trim()).filter(Boolean))].join(' · ') || null
        : orden.estado === 'COMPLETADA' ? orden.hecho : null;
    const otraCosa = fueOtraCosa(pidieron, seHizo);

    return (
        <ModalShell titulo={orden.lugar || orden.clienteNombre || orden.titulo || `Visita #${orden.id}`}
            subtitulo={(orden.lugar ? [orden.clienteNombre, otraCosa ? `Pidieron ${String(pidieron).toLowerCase()} → ${corto(seHizo).toLowerCase()}` : orden.motivo].filter(Boolean).join(' · ')
                : orden.titulo && orden.clienteNombre ? tituloSinCliente(orden.titulo, orden.clienteNombre) : '') || undefined}
            onCerrar={onCerrar} lateral
            pie={
                <div className="flex gap-2">
                    <button type="button" onClick={onCerrar}
                        className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cerrar</button>
                    {/* 28-oct-2026: el PDF del trabajo hecho, sin pasar por Trabajos */}
                    {/* PDF del trabajo: hecho (Trabajo realizado) o todavía no (presupuesto), igual que en Trabajos */}
                    {servicio && (
                        <button type="button" onClick={() => pdfDeTrabajo(servicio)}
                            className="flex-1 h-11 rounded-xl border border-black/10 dark:border-white/10 text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                            <LuFileText size={15} /> PDF
                        </button>
                    )}
                    {error !== 'borrado' && onVerTrabajos && <button type="button" onClick={verEnTrabajos}
                        className="flex-[2] h-11 rounded-xl bg-[#C9341F] text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                        Ver en Trabajos <LuArrowRight size={15} />
                    </button>}
                </div>
            }>
            <div className="space-y-4">
                <div className="rounded-2xl bg-panel px-4">
                    <div className={fila}>
                        <span className="text-caption text-muted">Visita</span>
                        <span className="flex items-center gap-2">
                            <Chip estado={orden.estado} />
                            {orden.estado === 'PENDIENTE' && !orden.sinConfirmar && (
                                <span className="text-caption font-bold text-muted">{aCoordinar ? 'falta elegir el día' : orden.confirmadaEn ? '✓ confirmó' : 'sin "ok, voy"'}</span>
                            )}
                        </span>
                    </div>
                    {abierta && (orden.sinConfirmar ? (
                        <div className="py-2.5 border-b border-black/[0.06] dark:border-white/[0.06] space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <EtiquetaTentativa />
                                <span className="text-caption text-muted text-right">Falta que confirme el cliente</span>
                            </div>
                            {/* Lo que respondió el técnico (20-oct-2026) */}
                            <p className={`text-caption font-bold ${orden.noPuedeEn ? 'text-[var(--alerta-tx)]' : orden.confirmadaEn ? 'text-[var(--success-tx)]' : 'text-muted'}`}>
                                {orden.noPuedeEn
                                    ? (/^Puede /.test(String(orden.noPuedeMotivo || ''))
                                        ? `✋ ${(orden.tecnicoNombre || 'El técnico').split(' ')[0]} no puede ese día. ${orden.noPuedeMotivo}: proponéselo al cliente y movela con "Otro día u hora"`
                                        : `✋ ${(orden.tecnicoNombre || 'El técnico').split(' ')[0]} no puede${orden.noPuedeMotivo ? ` (${orden.noPuedeMotivo})` : ''}: pasala a otro técnico o a otro día desde Trabajos`)
                                    : orden.confirmadaEn
                                        ? `👍 ${(orden.tecnicoNombre || 'El técnico').split(' ')[0]} puede ese día: confirmale al cliente`
                                        : `Esperando que ${(orden.tecnicoNombre || 'el técnico').split(' ')[0]} diga si puede`}
                            </p>
                            {/* 4-nov-2026: preguntarle al cliente sin escribir el mensaje cada vez */}
                            {waNumero(orden.clienteTelefono) && (
                                <a href={`https://wa.me/${waNumero(orden.clienteTelefono)}?text=${encodeURIComponent(mensajeConfirmar({
                                    cliente: orden.clienteNombre, fecha: orden.fechaProgramada, direccion: orden.direccion,
                                    franja: (horaTexto(orden.horaEstimada) || '').replace(/ h$/, '') || null }))}`} target="_blank" rel="noreferrer"
                                    className="w-full h-10 rounded-xl bg-[#1F7A46] text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                    💬 Preguntarle por WhatsApp
                                </a>
                            )}
                            <button type="button" disabled={moviendo} onClick={() => cambiarTentativa(false)}
                                className="w-full h-10 rounded-xl text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40"
                                style={{ background: 'var(--etapa-listo)' }}>
                                <LuCircleCheck size={15} /> El cliente confirmó · avisar al técnico
                            </button>
                        </div>
                    ) : orden.estado === 'PENDIENTE' && !aCoordinar && (
                        <div className="flex justify-end py-1.5 border-b border-black/[0.06] dark:border-white/[0.06]">
                            <button type="button" disabled={moviendo} onClick={() => cambiarTentativa(true)}
                                className="text-caption font-bold text-muted underline active:opacity-60 disabled:opacity-40">
                                Pasar a fecha tentativa
                            </button>
                        </div>
                    ))}
                    <div className={fila}>
                        <span className="text-caption text-muted">Cuándo</span>
                        {aCoordinar ? (
                            <span className="text-right text-body font-bold text-[var(--warning-tx)]">A coordinar<span className="block text-caption font-bold">Pueden: {resumenVentanas(orden.ventanasCliente).join(' / ') || 'sin días marcados'}</span></span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <span className="flex items-center gap-1.5 text-body font-bold text-ink"><LuClock size={14} />{fechaAR(orden.fechaProgramada)} · {hora}
                                    {orden.estado === 'COMPLETADA' && /T\d{2}:\d{2}/.test(String(orden.fechaCompletada || '')) && (
                                        <span className="text-caption font-bold text-muted">· cerrada {String(orden.fechaCompletada).slice(11, 16)}</span>
                                    )}
                                </span>
                            </span>
                        )}
                    </div>
                    <div className={fila}>
                        <span className="text-caption text-muted">Técnico</span>
                        <span className="flex items-center gap-1.5 text-body font-bold text-ink">
                            <AvatarTecnico nombre={orden.tecnicoNombre} size={20} />{orden.tecnicoNombre || 'Sin técnico'}
                        </span>
                    </div>
                    {/* 1-nov-2026: con fecha tentativa no hay "Salió" (el técnico tampoco puede salir): primero se confirma */}
                    {paso && !(orden.sinConfirmar && orden.estado === 'PENDIENTE') && (
                        <div className="flex gap-2 py-2.5 border-b border-black/[0.06] dark:border-white/[0.06]">
                            {pasoVisible && (
                                <button type="button" disabled={moviendo} onClick={() => (paso.salirAhora ? salirAhora() : paso.cerrar ? setCerrando(true) : mover(paso.estado))}
                                    className="flex-[2] h-10 rounded-xl bg-[#C9341F] text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                                    <paso.Icon size={15} /> {paso.label}
                                </button>
                            )}
                            {paso.atras && (
                                <button type="button" disabled={moviendo} onClick={() => mover(paso.atras)}
                                    className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black active:scale-95 disabled:opacity-40">Deshacer</button>
                            )}
                            {paso.salirAhora && (
                                <button type="button" disabled={moviendo} onClick={() => setEligiendoDia(true)}
                                    className="flex-1 h-10 rounded-xl bg-chip text-ink text-label font-black active:scale-95 disabled:opacity-40">Elegir otro día</button>
                            )}
                        </div>
                    )}
                    {['PENDIENTE', 'NO_ATENDIDO'].includes(orden.estado) && (
                        <div className="py-2.5 border-b border-black/[0.06] dark:border-white/[0.06]">
                            {espera ? (
                                <div className="space-y-2">
                                    <p className="text-caption text-secondary">
                                        Queda sin día hasta que el cliente confirme. {(orden.tecnicoNombre || 'El técnico').split(' ')[0]} recibe el aviso de que no vaya.
                                    </p>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setEspera(false)}
                                            className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">No</button>
                                        <button type="button" disabled={moviendo} onClick={() => dejarEnEspera()}
                                            className="flex-[2] h-10 rounded-xl bg-ink text-page text-label font-black active:scale-95 disabled:opacity-40">
                                            {moviendo ? 'Guardando…' : 'Sí, dejar en espera'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <button type="button" disabled={moviendo} onClick={() => setReprogramando(true)}
                                        className="flex-1 h-10 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                                        <LuCalendarClock size={15} /> Otro día u hora
                                    </button>
                                    <button type="button" disabled={moviendo} onClick={() => setEspera(true)}
                                        className="flex-1 h-10 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                                        <LuCirclePause size={15} /> Dejar en espera
                                    </button>
                                    {/* 29-oct-2026: fueron y no había nadie → vuelve a Por agendar y la empresa recibe el aviso */}
                                    {orden.estado !== 'NO_ATENDIDO' && (
                                        <button type="button" disabled={moviendo} onClick={() => dejarEnEspera('NO_ESTABA')}
                                            className="flex-1 h-10 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                                            No estaba
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Dónde: se puede corregir la dirección ahí mismo mientras la visita no arrancó (27-oct-2026) */}
                    {dirEdit !== null ? (
                        <div className="py-2.5 border-b border-black/[0.06] dark:border-white/[0.06] last:border-0 space-y-2">
                            <span className="text-caption text-muted">Dónde</span>
                            <input autoFocus value={dirEdit} onChange={e => setDirEdit(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') guardarDireccion(); if (e.key === 'Escape') setDirEdit(null); }}
                                placeholder="Calle y número, localidad"
                                className="w-full h-11 px-3 rounded-xl bg-page text-ink text-body font-bold outline-none border border-black/10 dark:border-white/10 focus:border-[#C9341F]/50" />
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setDirEdit(null)}
                                    className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cancelar</button>
                                <button type="button" disabled={moviendo} onClick={guardarDireccion}
                                    className="flex-[2] h-10 rounded-xl bg-[#C9341F] text-white text-label font-black active:scale-95 disabled:opacity-40">
                                    {moviendo ? 'Guardando…' : 'Guardar dirección'}
                                </button>
                            </div>
                        </div>
                    ) : (orden.direccion || abierta) && (
                        <div className={fila}>
                            <span className="text-caption text-muted">Dónde</span>
                            <span className="flex items-center gap-2 min-w-0">
                                {orden.direccion ? (
                                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(orden.direccion)}`} target="_blank" rel="noreferrer"
                                        className="flex items-center gap-1.5 text-body font-bold text-ink underline text-right min-w-0"><LuMapPin size={14} className="shrink-0" />{orden.direccion}</a>
                                ) : <span className="text-body font-bold text-muted">Sin dirección</span>}
                                {abierta && (
                                    <button type="button" onClick={() => setDirEdit(orden.direccion || '')} aria-label="Cambiar la dirección"
                                        className="shrink-0 w-9 h-9 rounded-lg bg-chip text-secondary inline-flex items-center justify-center active:scale-95">
                                        <LuPencil size={14} />
                                    </button>
                                )}
                            </span>
                        </div>
                    )}
                </div>

                {orden.tecnicoNombre && <ChatVisita orden={orden} soyAdmin />}

                <PedidoVsHecho pedido={pidieron} hecho={seHizo} />

                <ReclamoEnFicha orden={orden} onCambio={onCambio} onActualizar={(d) => setOrden(o => ({ ...o, ...d }))} />

                {trabajoId && (
                    cargando ? <div className="h-24 rounded-2xl bg-panel animate-pulse" /> : servicio ? (
                        <div className="rounded-2xl bg-panel px-4">
                            <div className={fila}>
                                <span className="text-caption text-muted">Trabajo #{servicio.id}</span>
                                <Chip estado={servicio.estado} />
                            </div>
                            <div className={fila}>
                                <span className="text-caption text-muted">{servicio.estado === 'COBRADO' ? 'Cobrado' : 'Total'}</span>
                                <span className="text-right">
                                    <M valor={total} className="text-body-lg font-black text-ink" />
                                    {servicio.modalidadCobro && <span className="block text-caption text-muted">{MODALIDAD[servicio.modalidadCobro] || servicio.modalidadCobro}</span>}
                                </span>
                            </div>
                            {(servicio.items || []).length > 0 && (
                                <div className="py-2.5 space-y-1.5">
                                    {servicio.items.map((it, i) => (
                                        <div key={i} className="flex justify-between gap-3 text-caption">
                                            <span className="min-w-0 text-secondary">
                                                {it.equipoSerial && <span className="font-black text-ink mr-1.5">{it.equipoSerial}</span>}
                                                {it.trabajoRealizado || 'Trabajo'}
                                            </span>
                                            <M valor={Number(it.costo || 0)} className="shrink-0 font-bold text-ink" />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {servicio.observaciones && <p className="py-2.5 text-caption text-muted border-t border-black/[0.06] dark:border-white/[0.06]">{servicio.observaciones}</p>}
                        </div>
                    ) : error === 'borrado' ? (
                        <div className="rounded-2xl px-4 py-3 bg-[color:var(--alerta-bg)] text-[color:var(--alerta-tx)] text-caption font-bold">
                            El trabajo de esta visita ya no existe (se borró). Podés eliminar la visita para que no aparezca más.
                        </div>
                    ) : <p className="text-caption text-muted text-center">{error || 'No se pudo cargar el trabajo.'}</p>
                )}

                {/* Lo que mandó el cliente (y, con la visita hecha, lo que cargó el técnico): lo mismo que ve el técnico */}
                <AntesDeIr ordenId={orden.id} hecha={orden.estado === 'COMPLETADA'} />

                {/* Eliminar la visita (con confirmación en la misma ficha) */}
                {confirmar ? (
                    <div className="flex items-center gap-2 p-3 rounded-2xl border border-[#C9341F]/40">
                        <p className="flex-1 text-caption font-bold text-ink">¿Eliminar esta visita? No se puede deshacer.</p>
                        <button type="button" onClick={() => setConfirmar(false)} className="h-9 px-3 rounded-xl bg-chip text-secondary text-label font-black">No</button>
                        <button type="button" onClick={eliminar} disabled={borrando}
                            className="h-9 px-3 rounded-xl bg-[#C9341F] text-white text-label font-black disabled:opacity-50">{borrando ? 'Borrando…' : 'Sí, eliminar'}</button>
                    </div>
                ) : (
                    <button type="button" onClick={() => setConfirmar(true)}
                        className={`w-full h-10 rounded-xl inline-flex items-center justify-center gap-1.5 text-label font-black active:scale-95 ${error === 'borrado' ? 'bg-[#C9341F] text-white' : 'text-brand-red border border-black/10 dark:border-white/10'}`}>
                        <LuTrash2 size={14} /> Eliminar visita
                    </button>
                )}
                {!trabajoId && <p className="text-caption text-muted text-center">Visita suelta, sin presupuesto cargado.</p>}
            </div>
            {eligiendoDia && (
                <ConfirmarHorarioSheet
                    servicio={{ id: orden.presupuestoId, ventanasDisponibles: orden.ventanasCliente, clienteNombre: orden.clienteNombre }}
                    url={orden.presupuestoId ? null : `/ordenes/${orden.id}/confirmar-horario`}
                    onCerrar={() => setEligiendoDia(false)}
                    onConfirmado={async () => {
                        setEligiendoDia(false);
                        try { const r = await api.get(`/ordenes/${orden.id}`); setOrden(o => ({ ...o, ...r.data })); }
                        catch { setOrden(o => ({ ...o, horarioACoordinar: false })); }
                        onCambio?.();
                    }} />
            )}
            {reprogramando && (
                <ReprogramarSheet orden={orden} tecnicos={tecnicos}
                    onCerrar={() => setReprogramando(false)}
                    onGuardado={async () => {
                        setReprogramando(false);
                        try { const r = await api.get(`/ordenes/${orden.id}`); setOrden(o => ({ ...o, ...r.data })); } catch { /* se ve al recargar */ }
                        onCambio?.();
                    }} />
            )}
            {/* Pedido de empresa o cliente con tarifa mensual: carga por N/S, sin cobro */}
            {cerrando && orden.tarifaMensual && (
                <CargaPorSerieSheet orden={orden} onClose={() => setCerrando(false)}
                    onGuardado={async () => {
                        try { await api.patch(`/ordenes/${orden.id}/estado`, { estado: 'COMPLETADA' }); }
                        catch { toast.error('El trabajo quedó cargado, pero no se pudo cerrar la visita'); }
                        setOrden(o => ({ ...o, estado: 'COMPLETADA' })); onCambio?.();
                    }} />
            )}
            {cerrando && !orden.tarifaMensual && (
                <ModalRegistrarTrabajo orden={orden} tecnicoId={orden.tecnicoId}
                    onCerrar={() => setCerrando(false)}
                    onGuardado={() => { setCerrando(false); setOrden(o => ({ ...o, estado: 'COMPLETADA' })); onCambio?.(); }} />
            )}
        </ModalShell>
    );
}
